import { memo } from "react";
import { Box, SxProps, TableCell, TableCellProps } from "@mui/material";
import { Theme } from "@mui/system";
import { TABLE_CELL_MIN_WIDTH } from "src/components-mui/base/table-scrollable/constants";
import { useTableScrollableSnapshot } from "src/components-mui/base/table-scrollable/store/table-scrollable-store";

export interface ITableScrollableCellProps extends Omit<TableCellProps, "width"> {
    shrinkable?: boolean;
    width?: string | number | number[] | string[];
    contentSx?: SxProps<Theme>;
}

interface ITableScrollableCellComponentProps extends ITableScrollableCellProps {
    shrinkFirstColumn?: boolean;
    firstColumnShrunkWidth?: number;
    isShrinkAnimationEnabled?: boolean;
}

const TableScrollableCellComponent = ({
    children,
    sx,
    shrinkable,
    shrinkFirstColumn,
    firstColumnShrunkWidth,
    isShrinkAnimationEnabled,
    width,
    contentSx,
    ...props
}: ITableScrollableCellComponentProps) => {
    const shrinkableCellSx: SxProps<Theme> = shrinkable
        ? {
              position: "sticky",
              left: 0,
              zIndex: 1,
              borderRight: ["2px solid var(--mui-palette-gray-100)", "2px solid var(--mui-palette-gray-100)", "none"],
              ...(shrinkable && isShrinkAnimationEnabled ? { transition: "padding .3s" } : {}),
              "&::after": {
                  content: shrinkFirstColumn ? "''" : "none",
                  display: "block",
                  position: "absolute",
                  width: "16px",
                  height: "100%",
                  pointerEvents: "none",
                  top: 0,
                  right: "-18px",
                  boxShadow: "rgb(230, 230, 230) 10px 0px 8px -8px inset",
              },
          }
        : {};

    return (
        <TableCell
            sx={{
                paddingY: ["10px", "10px", 2],
                paddingX: shrinkable ? [0.5, 0.5, 1] : [1],
                minWidth: TABLE_CELL_MIN_WIDTH,
                ...shrinkableCellSx,
                ...sx,
            }}
            {...props}
        >
            <Box
                sx={{
                    width: shrinkable && shrinkFirstColumn ? firstColumnShrunkWidth : width,
                    display: "flex",
                    gap: 2,
                    textAlign: shrinkable ? "left" : "right",
                    justifyContent: shrinkable ? "flex-start" : "flex-end",
                    alignItems: "center",
                    ...(shrinkable && isShrinkAnimationEnabled ? { transition: "width .3s" } : {}),
                    ...contentSx,
                }}
            >
                {children}
            </Box>
        </TableCell>
    );
};

const MemoizedTableScrollableCell = memo(TableScrollableCellComponent);

const ShrinkableTableScrollableCell = (props: ITableScrollableCellProps) => {
    const { shrinkFirstColumn, firstColumnShrunkWidth, isShrinkAnimationEnabled } = useTableScrollableSnapshot();

    return (
        <MemoizedTableScrollableCell
            {...props}
            shrinkFirstColumn={shrinkFirstColumn}
            firstColumnShrunkWidth={firstColumnShrunkWidth}
            isShrinkAnimationEnabled={isShrinkAnimationEnabled}
        />
    );
};

export const TableScrollableCell = (props: ITableScrollableCellProps) => {
    if (props.shrinkable) {
        return <ShrinkableTableScrollableCell {...props} />;
    }

    return <MemoizedTableScrollableCell {...props} />;
};
